import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

// Güvenli JSON parse — servis HTML döndürdüğünde crash'i önler
const safeJson = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(`Unexpected response (HTTP ${response.status}): ${text.substring(0, 120)}`);
  }
  return response.json();
};

const QRPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab') || 'qr';

  const [qrData, setQrData] = useState(null);
  const [status, setStatus] = useState('checking'); // checking, idle, preparing, qr_ready, connected, error
  const [error, setError] = useState(null);
  const [qrTimer, setQrTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  // Telefon ile bağlanma state'leri
  const [firmPhone, setFirmPhone] = useState(''); // Firmaya kayıtlı telefon
  const [pairingCode, setPairingCode] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState(null);

  // Timer ve polling ref'leri — cleanup için
  const qrTimerRef = useRef(null);
  const statusPollRef = useRef(null);

  // Sayfa yüklendiğinde mevcut durumu kontrol et
  useEffect(() => {
    checkCurrentStatus();
    return () => {
      clearInterval(qrTimerRef.current);
      clearInterval(statusPollRef.current);
    };
  }, []);

  // QR gösterildiğinde bağlanma durumunu periyodik kontrol et
  const startStatusPolling = () => {
    clearInterval(statusPollRef.current);
    statusPollRef.current = setInterval(async () => {
      try {
        const response = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/status`);
        const data = await safeJson(response);
        if (data.success && (data.data?.status === 'connected' || data.data?.status === 'authenticated' || data.data?.connected)) {
          clearInterval(statusPollRef.current);
          clearInterval(qrTimerRef.current);
          setIsTimerRunning(false);
          setStatus('connected');
          setQrData(null);
          console.log('🎉 WhatsApp bağlantısı tespit edildi (polling)');
        }
      } catch (e) {
        // Sessizce geç — servis geçici olarak cevap vermeyebilir
      }
    }, 10000); // 10 saniyede bir kontrol — 3s log spam'ini önlemek için
  };

  // Mevcut WhatsApp durumunu kontrol et
  const checkCurrentStatus = async () => {
    setStatus('checking');
    setError(null);
    
    try {
      console.log('🔍 Mevcut WhatsApp durumu kontrol ediliyor...');
      
      const response = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/status`);
      const data = await safeJson(response);
      
      if (data.success) {
        console.log('✅ Durum kontrolü başarılı:', data.data.status);

        // Firma telefon numarasını kaydet (status endpoint'ten geliyor)
        if (data.data.connected_phone && data.data.connected_phone !== 'Bilinmiyor') {
          setFirmPhone(data.data.connected_phone);
        }

        if (data.data.status === 'connected' || data.data.status === 'authenticated' || data.data.connected) {
          setStatus('connected');
          setError(null);
          console.log('🎉 WhatsApp zaten bağlı!');
        } else {
          // qr_waiting, initializing, idle — hepsinde idle göster, kullanıcı başlatır
          setStatus('idle');
          console.log('📱 WhatsApp bağlı değil, Başlat butonunu bekliyor');
        }
      } else {
        console.error('❌ Durum kontrolü başarısız:', data.error);
        setStatus('idle');
      }
      
    } catch (err) {
      console.error('❌ Durum kontrol hatası:', err);
      setStatus('idle');
    }
  };

  // Mevcut QR'ı getir (eğer qr_waiting durumundaysa)
  const fetchExistingQR = async () => {
    try {
      console.log('📱 Mevcut QR kontrol ediliyor...');

      const response = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/qr`);
      const data = await safeJson(response);

      if (data.success && data.data && data.data.qr_code) {
        console.log('✅ Mevcut QR bulundu, image URL kullanılıyor');
        // PNG image endpoint'ini kullan — raw string img src olmaz
        const imageUrl = `https://panel.fbajans.com/api/qr-image/${slug}?t=${Date.now()}`;
        setQrData(imageUrl);
        setStatus('qr_ready');
        startQRTimer();
        startStatusPolling();
      } else {
        // QR bulunamadı — browser henüz başlatılmamış olabilir (qr_waiting status ama browser yok)
        // init çağır ve QR'ı bekle
        console.log('⚠️ Mevcut QR bulunamadı, browser başlatılıyor...');
        await startQRProcess();
      }

    } catch (err) {
      console.error('❌ Mevcut QR kontrol hatası:', err);
      setStatus('idle');
    }
  };

  // QR timer'ı başlat — süre dolunca otomatik yenile
  const startQRTimer = () => {
    clearInterval(qrTimerRef.current);
    setQrTimer(30);
    setIsTimerRunning(true);
    
    qrTimerRef.current = setInterval(() => {
      setQrTimer(prev => {
        if (prev <= 1) {
          clearInterval(qrTimerRef.current);
          setIsTimerRunning(false);
          setStatus('qr_expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Fetch + QR göster yardımcısı (startQRProcess ve refreshQR'da ortak kullanılır)
  // /api/qr/:slug → raw data-ref string döner (img src olarak çalışmaz)
  // /api/qr-image/:slug → PNG binary döner (img src olarak direkt kullanılır)
  const fetchAndShowQR = async () => {
    const MAX_ATTEMPTS = 20;
    for (let i = 1; i <= MAX_ATTEMPTS; i++) {
      // İlk denemede hemen bak, sonrakilerde 3s bekle
      if (i > 1) await new Promise(r => setTimeout(r, 3000));
      try {
        // Önce QR hazır mı kontrol et (JSON endpoint)
        const qrResponse = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/qr`);
        const qrJson = await safeJson(qrResponse);
        // Backend qr_code'u doğrudan üst seviyede döndürür (data katmanı yok)
        const qrCode = qrJson.qr_code || qrJson.data?.qr_code;
        if (qrJson.success && qrCode) {
          // QR hazır — base64 data URI veya PNG image endpoint'ini kullan
          const imageUrl = qrCode.startsWith('data:')
            ? qrCode
            : `https://panel.fbajans.com/api/qr-image/${slug}?t=${Date.now()}`;
          console.log(`✅ QR kod hazır (deneme ${i}), image URL: ${imageUrl.substring(0, 60)}...`);
          setQrData(imageUrl);
          setStatus('qr_ready');
          startQRTimer();
          startStatusPolling();
          return;
        }
        console.log(`⏳ QR henüz hazır değil (deneme ${i}/${MAX_ATTEMPTS}): ${qrJson.error || 'bekleniyor'}`);
      } catch (err) {
        console.warn(`⚠️ QR deneme ${i} hatası:`, err.message);
      }
    }
    setError('QR kod alınamadı: 60 saniye içinde oluşturulamadı');
    setStatus('error');
  };

  // QR başlatma butonu
  const startQRProcess = async () => {
    setStatus('preparing');
    setError(null);
    setQrData(null);
    clearInterval(qrTimerRef.current);
    clearInterval(statusPollRef.current);
    
    try {
      console.log('🚀 QR süreci başlatılıyor...');
      
      const initResponse = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/init`, {
        method: 'POST'
      });
      
      if (initResponse.ok) {
        console.log('✅ WhatsApp başlatıldı, QR bekleniyor...');
        await fetchAndShowQR();
      } else {
        const initError = await safeJson(initResponse).catch(() => ({}));
        console.error('❌ WhatsApp başlatılamadı:', initError);
        setError('WhatsApp başlatılamadı: ' + (initError.error || `HTTP ${initResponse.status}`));
        setStatus('error');
      }
      
    } catch (err) {
      console.error('❌ QR süreç hatası:', err);
      setError('QR süreç hatası: ' + err.message);
      setStatus('error');
    }
  };

  // QR yenileme
  const refreshQR = async () => {
    setStatus('preparing');
    setError(null);
    setQrData(null);
    clearInterval(qrTimerRef.current);
    clearInterval(statusPollRef.current);
    
    try {
      console.log('🔄 QR yenileniyor...');
      
      const clearResponse = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/disconnect`, {
        method: 'POST'
      });
      
      if (clearResponse.ok) {
        console.log('✅ Eski QR temizlendi, yeni init başlatılıyor...');
        await new Promise(r => setTimeout(r, 2000));
        
        const initResponse = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/init`, {
          method: 'POST'
        });
        
        if (initResponse.ok) {
          console.log('✅ WhatsApp yeniden başlatıldı, QR bekleniyor...');
          await fetchAndShowQR();
        } else {
          const initError = await safeJson(initResponse).catch(() => ({}));
          setError('WhatsApp yeniden başlatılamadı: ' + (initError.error || `HTTP ${initResponse.status}`));
          setStatus('error');
        }
      } else {
        setError('Eski QR temizlenemedi');
        setStatus('error');
      }
      
    } catch (err) {
      setError('QR yenileme hatası: ' + err.message);
      setStatus('error');
    }
  };

  // Durum içeriği
  const getStatusContent = () => {
    switch (status) {
      case 'checking':
        return {
          icon: '🔍',
          title: 'Durum Kontrol Ediliyor...',
          desc: 'WhatsApp bağlantı durumu kontrol ediliyor',
          color: '#17a2b8',
          bgColor: '#d1ecf1'
        };
      
      case 'idle':
        return {
          icon: '🚀',
          title: 'QR Kod Başlatma',
          desc: 'WhatsApp QR kodunu başlatmak için butona tıklayın',
          color: '#007bff',
          bgColor: '#e3f2fd'
        };
      
      case 'preparing':
        return {
          icon: '⏳',
          title: 'QR Kod Hazırlanıyor...',
          desc: 'WhatsApp oturumu başlatılıyor ve QR kod oluşturuluyor',
          color: '#856404',
          bgColor: '#fff3cd'
        };
      
      case 'qr_ready':
        return {
          icon: '📱',
          title: 'QR Kod Hazır!',
          desc: `QR kodu telefonda tarayın. Kalan süre: ${qrTimer} saniye`,
          color: '#155724',
          bgColor: '#d4edda'
        };
      
      case 'qr_expired':
        return {
          icon: '⏰',
          title: 'QR Kod Süresi Doldu',
          desc: 'QR kod süresi doldu. Yeni QR kod için yenile butonuna tıklayın',
          color: '#856404',
          bgColor: '#fff3cd'
        };
      
      case 'connected':
        return {
          icon: '✅',
          title: 'WhatsApp Zaten Bağlı!',
          desc: 'WhatsApp hesabınız zaten bağlı. QR kod taramanıza gerek yok!',
          color: '#155724',
          bgColor: '#d4edda'
        };
      
      case 'error':
        return {
          icon: '❌',
          title: 'Hata Oluştu',
          desc: error || 'Bilinmeyen hata oluştu',
          color: '#721c24',
          bgColor: '#f8d7da'
        };
      
      default:
        return {
          icon: '❓',
          title: 'Bilinmeyen Durum',
          desc: 'Sistem durumu belirlenemedi',
          color: '#6c757d',
          bgColor: '#f8f9fa'
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: 20,
        padding: 40,
        maxWidth: 600,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
      }}>
        
        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <i className="fas fa-mobile-alt" style={{ fontSize: 60, marginBottom: 20 }}></i>
          <h1 style={{ 
            margin: 0, 
            color: '#128C7E', 
            fontSize: 32,
            fontWeight: 'bold'
          }}>
            FBAJANS WhatsApp
          </h1>
          <p style={{ 
            margin: '15px 0 0 0', 
            color: '#666', 
            fontSize: 18,
            fontWeight: '500'
          }}>
            Firma: <span style={{ color: '#25D366' }}>{slug}</span>
          </p>
        </div>

        {/* Tab Butonları */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 30, borderRadius: 50, background: '#f0f0f0', padding: 5 }}>
          <button
            onClick={() => navigate(`/qr/${slug}?tab=qr`)}
            style={{
              flex: 1, padding: '12px 20px', borderRadius: 50, border: 'none', cursor: 'pointer',
              background: activeTab === 'qr' ? '#25D366' : 'transparent',
              color: activeTab === 'qr' ? 'white' : '#555',
              fontWeight: 'bold', fontSize: 14, transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-qrcode" style={{ marginRight: 8 }}></i>
            QR Kodu ile Bağlan
          </button>
          <button
            onClick={() => navigate(`/qr/${slug}?tab=phone`)}
            style={{
              flex: 1, padding: '12px 20px', borderRadius: 50, border: 'none', cursor: 'pointer',
              background: activeTab === 'phone' ? '#25D366' : 'transparent',
              color: activeTab === 'phone' ? 'white' : '#555',
              fontWeight: 'bold', fontSize: 14, transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-mobile-alt" style={{ marginRight: 8 }}></i>
            Telefon Numarası ile Bağlan
          </button>
        </div>

        {/* Bilgi Kutusu */}
        <div style={{
          background: '#d1ecf1',
          border: '1px solid #bee5eb',
          padding: 20,
          borderRadius: 10,
          marginBottom: 30,
          color: '#0c5460'
        }}>
          <h3 style={{ margin: '0 0 15px 0' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            Akıllı Durum Kontrolü
          </h3>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <p style={{ margin: '0 0 10px 0' }}>
              <strong>Nasıl Çalışır?</strong>
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, textAlign: 'left' }}>
              <li><i className="fas fa-search" style={{ marginRight: '8px' }}></i>Önce mevcut durum kontrol edilir</li>
              <li><i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>Zaten bağlıysa QR gerekmez</li>
              <li><i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>Bağlı değilse "QR Başlat" ile başlatın</li>
              <li><i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>30 saniye sonra QR kod görünür</li>
            </ul>
            <p style={{ margin: '10px 0 0 0', fontSize: 12, fontStyle: 'italic' }}>
              <strong>Avantaj:</strong> Gereksiz QR oluşturma yok, akıllı kontrol!
            </p>
          </div>
        </div>

        {/* Telefon Numarası Tab İçeriği */}
        {activeTab === 'phone' && (
          <div style={{ marginBottom: 30 }}>
            {/* Pairing code göster */}
            {pairingCode ? (
              <div style={{
                background: '#d4edda', border: '2px solid #25D366',
                padding: 30, borderRadius: 15, textAlign: 'center'
              }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
                <h2 style={{ margin: '0 0 8px 0', color: '#155724' }}>Bağlantı Kodu Hazır!</h2>
                <p style={{ color: '#155724', marginBottom: 20 }}>
                  WhatsApp uygulamasında bu kodu girin: <strong>Ayarlar → Bağlı Cihazlar → Cihaz Bağla → Telefon Numarasıyla Bağla</strong>
                </p>
                <div style={{
                  background: 'white', border: '2px dashed #25D366',
                  borderRadius: 12, padding: '20px 40px', display: 'inline-block',
                  fontSize: 36, fontWeight: 'bold', letterSpacing: 8,
                  color: '#111', fontFamily: 'monospace'
                }}>
                  {pairingCode}
                </div>
                <p style={{ color: '#6c757d', marginTop: 15, fontSize: 13 }}>
                  Bu kod birkaç dakika içinde geçersiz olur.
                </p>
                <button
                  onClick={() => { setPairingCode(null); setPhoneError(null); setPhoneNumber(''); }}
                  style={{
                    marginTop: 15, background: '#6c757d', color: 'white', border: 'none',
                    padding: '10px 25px', borderRadius: 50, cursor: 'pointer', fontWeight: 'bold'
                  }}
                >
                  Yeni Kod İste
                </button>
              </div>
            ) : (
              <div style={{
                background: 'white', border: '1px solid #dee2e6',
                padding: 30, borderRadius: 15, textAlign: 'center'
              }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>📱</div>
                <h2 style={{ margin: '0 0 8px 0', color: '#111' }}>Telefon Numarası ile Bağlan</h2>
                <p style={{ color: '#6c757d', marginBottom: 10, fontSize: 14 }}>
                  Firmaya kayıtlı telefon numarası kullanılacak:
                </p>
                <div style={{
                  background: '#f8f9fa', border: '1px solid #dee2e6',
                  borderRadius: 8, padding: '10px 20px', display: 'inline-block',
                  fontSize: 20, fontWeight: 'bold', fontFamily: 'monospace',
                  marginBottom: 25, letterSpacing: 2
                }}>
                  {firmPhone ? `+90${firmPhone.replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '')}` : '...'}
                </div>

                {phoneError && (
                  <div style={{
                    background: '#f8d7da', border: '1px solid #f5c6cb',
                    borderRadius: 8, padding: '10px 15px', marginBottom: 20,
                    color: '#721c24', fontSize: 14
                  }}>
                    ❌ {phoneError}
                  </div>
                )}

                <div style={{ marginBottom: 25 }}>
                  <button
                    onClick={async () => {
                      setPhoneLoading(true);
                      setPhoneError(null);
                      setPairingCode(null);
                      try {
                        // Önce browser'ı başlat (qr_waiting durumuna getir)
                        await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/init`, { method: 'POST' });
                        await new Promise(r => setTimeout(r, 3000));

                        const phone = firmPhone || '';
                        if (!phone) throw new Error('Firma telefon numarası bulunamadı');
                        // Normalize: baştaki 0'ı kaldır, 90 prefix'i tekrar ekleme
                        let digits = phone.replace(/\D/g, '');
                        if (digits.startsWith('0')) digits = digits.substring(1); // 05433... → 5433...
                        const fullPhone = digits.startsWith('90') ? digits : '90' + digits; // → 905433...

                        const res = await fetch(`https://panel.fbajans.com/api/whatsapp/${slug}/phone-pair`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ phone: fullPhone })
                        });
                        const data = await safeJson(res);
                        if (data.success && data.data?.pairing_code) {
                          setPairingCode(data.data.pairing_code);
                        } else {
                          setPhoneError(data.error || 'Kod alınamadı');
                        }
                      } catch (err) {
                        setPhoneError(err.message || 'Bağlantı hatası');
                      } finally {
                        setPhoneLoading(false);
                      }
                    }}
                    disabled={phoneLoading || !firmPhone}
                    style={{
                      background: phoneLoading || !firmPhone ? '#aaa' : '#25D366',
                      color: 'white', border: 'none', padding: '14px 40px',
                      borderRadius: 50, cursor: phoneLoading || !firmPhone ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold', fontSize: 16, whiteSpace: 'nowrap'
                    }}
                  >
                    {phoneLoading ? '⏳ Kod Oluşturuluyor...' : '🔑 Bağlantı Kodu Oluştur'}
                  </button>
                </div>

                <div style={{
                  background: '#e8f5e9', borderRadius: 10, padding: '12px 20px',
                  fontSize: 13, color: '#2e7d32', textAlign: 'left', maxWidth: 400, margin: '0 auto'
                }}>
                  <strong>Nasıl kullanılır?</strong>
                  <ol style={{ margin: '8px 0 0 0', paddingLeft: 18 }}>
                    <li>Aşağıdaki "Bağlantı Kodu Oluştur" butonuna basın</li>
                    <li>WhatsApp uygulamasını açın</li>
                    <li>Ayarlar → Bağlı Cihazlar → Cihaz Bağla</li>
                    <li>"Telefon numarasıyla bağla" seçeneğine dokunun</li>
                    <li>Oluşan kodu girin</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Durum Göstergesi — sadece QR tab'ında göster */}
        {activeTab === 'qr' && (
          <div style={{
            background: statusContent.bgColor,
            color: statusContent.color,
            padding: 30,
            borderRadius: 15,
            marginBottom: 30,
            border: `1px solid ${statusContent.color}33`
          }}>
            <div style={{ fontSize: 48, marginBottom: 15 }}>{statusContent.icon}</div>
            <h2 style={{ margin: '0 0 10px 0', fontSize: 24 }}>{statusContent.title}</h2>
            <p style={{ margin: 0, fontSize: 16 }}>{statusContent.desc}</p>
            
            {/* QR Timer */}
            {status === 'qr_ready' && isTimerRunning && (
              <div style={{ 
                marginTop: 20, 
                fontSize: 18, 
                fontWeight: 'bold',
                color: qrTimer <= 10 ? '#dc3545' : '#28a745'
              }}>
                <i className="fas fa-clock" style={{ marginRight: '8px' }}></i>
                Kalan Süre: {qrTimer} saniye
              </div>
            )}
          </div>
        )}

        {/* QR Kod Gösterimi */}
        {activeTab === 'qr' && qrData && status === 'qr_ready' && (
          <div style={{ marginBottom: 30 }}>
            <div style={{
              background: 'white',
              padding: 20,
              borderRadius: 15,
              border: '2px solid #25D366',
              display: 'inline-block',
              boxShadow: '0 4px 15px rgba(37, 211, 102, 0.2)'
            }}>
              <img
                src={qrData}
                alt="WhatsApp QR Code"
                style={{
                  width: 256,
                  height: 256,
                  display: 'block'
                }}
              />
            </div>
            <div style={{ marginTop: 20, color: '#0c5460' }}>
              <p><strong><i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>QR Kod Nasıl Taranır?</strong></p>
              <ol style={{ textAlign: 'left', display: 'inline-block', fontSize: 14 }}>
                <li>WhatsApp uygulamasını açın</li>
                <li>Ayarlar → Bağlı Cihazlar</li>
                <li>"Cihaz Bağla" butonuna basın</li>
                <li>Bu QR kodu tarayın</li>
              </ol>
            </div>
          </div>
        )}

        {/* Ana Butonlar — sadece QR tab'ında */}
        {activeTab === 'qr' && (<div style={{ marginBottom: 30 }}>
          {status === 'checking' && (
            <button
              disabled
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '20px 40px',
                fontSize: 18,
                fontWeight: 'bold',
                borderRadius: 50,
                cursor: 'not-allowed',
                opacity: 0.7
              }}
            >
              <i className="fas fa-search" style={{ marginRight: '8px' }}></i>
              Kontrol Ediliyor...
            </button>
          )}

          {status === 'idle' && (
            <button
              onClick={startQRProcess}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '20px 40px',
                fontSize: 18,
                fontWeight: 'bold',
                borderRadius: 50,
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 123, 255, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(0, 123, 255, 0.3)';
              }}
            >
              <i className="fas fa-rocket" style={{ marginRight: '8px' }}></i>
              QR Başlat
            </button>
          )}

          {status === 'preparing' && (
            <button
              disabled
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '20px 40px',
                fontSize: 18,
                fontWeight: 'bold',
                borderRadius: 50,
                cursor: 'not-allowed',
                opacity: 0.7
              }}
            >
              ⏳ Hazırlanıyor...
            </button>
          )}

          {status === 'qr_ready' && (
            <div style={{ display: 'flex', gap: 15, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.close()}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  borderRadius: 50,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
                }}
              >
                <i className="fas fa-check" style={{ marginRight: '8px' }}></i>
                Sayfayı Kapat
              </button>
              <button
                onClick={() => window.open('/', '_blank')}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  borderRadius: 50,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)'
                }}
              >
                <i className="fas fa-home" style={{ marginRight: '8px' }}></i>
                Ana Panele Git
              </button>
            </div>
          )}

          {status === 'connected' && (
            <div style={{ display: 'flex', gap: 15, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.close()}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  borderRadius: 50,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
                }}
              >
                <i className="fas fa-check" style={{ marginRight: '8px' }}></i>
                Sayfayı Kapat
              </button>
              <button
                onClick={() => window.open('/', '_blank')}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  borderRadius: 50,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)'
                }}
              >
                <i className="fas fa-home" style={{ marginRight: '8px' }}></i>
                Ana Panele Git
              </button>
            </div>
          )}

          {status === 'qr_expired' && (
            <button
              onClick={refreshQR}
              style={{
                background: '#ffc107',
                color: '#212529',
                border: 'none',
                padding: '15px 30px',
                fontSize: 16,
                fontWeight: 'bold',
                borderRadius: 50,
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(255, 193, 7, 0.3)'
              }}
            >
              <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
              Yeni QR Al
            </button>
          )}

          {status === 'error' && (
            <div style={{ display: 'flex', gap: 15, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={startQRProcess}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  borderRadius: 50,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)'
                }}
              >
                <i className="fas fa-redo" style={{ marginRight: '8px' }}></i>
                Tekrar Dene
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  borderRadius: 50,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)'
                }}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                Sayfayı Yenile
              </button>
            </div>
          )}
        </div>)}

        {/* Footer */}
        <div style={{ 
          marginTop: 30, 
          paddingTop: 20, 
          borderTop: '1px solid #eee',
          color: '#999',
          fontSize: 12
        }}>
          <p style={{ margin: 0 }}>
            <i className="fas fa-shield-alt" style={{ marginRight: '8px' }}></i>
            <strong>FBAJANS</strong> - Akıllı WhatsApp QR Sistemi
          </p>
        </div>
      </div>
    </div>
  );
};

export default QRPage;
